<template>
  <div class="field-container">
    <!-- Text field -->
    <v-text-field
      v-if="field.type === 'textfield'"
      ref="field"
      :label="field.label"
      :placeholder="field.placeholder"
      :prepend-icon="field.prependIcon"
      :clearable="field.clearable"
      :required="field.required"
      outlined
      dense
      background-color="white"
      :value="value"
      @input="updateValue"
      :error="hasError || !!field.validationError"
      :error-messages="getErrorMessages"
      :rules="safeTextFieldRules"
    ></v-text-field>

    <!-- URL -->
    <v-text-field
      v-if="field.type === 'url'"
      ref="field"
      :label="field.label"
      :placeholder="field.placeholder"
      :prepend-icon="field.prependIcon"
      :clearable="field.clearable"
      :required="field.required"
      outlined
      dense
      background-color="white"
      :value="value"
      @input="updateValue"
      :error="hasError || !!field.validationError"
      :error-messages="getErrorMessages"
      :rules="validationRules"
    ></v-text-field>

    <!-- Textarea -->
    <v-textarea
      v-if="field.type === 'textarea'"
      :label="field.label"
      :placeholder="field.placeholder"
      :clearable="field.clearable"
      :required="field.required"
      outlined
      dense
      background-color="white"
      :value="value"
      @input="updateValue"
      :error="hasError || !!field.validationError"
      :error-messages="getErrorMessages"
      :rules="validationRules"
    ></v-textarea>
    <!-- Dropdown / Autocomplete -->
    <v-autocomplete
      v-if="field.type === 'dropdown'"
      :items="getFieldOptions"
      :label="field.label"
      :placeholder="field.placeholder"
      :clearable="field.clearable"
      :required="field.required"
      outlined
      dense
      background-color="white"
      :item-text="getItemText"
      :item-value="getItemValue"
      :value="getSelectedValue"
      @input="handleDropdownChange"
      return-object
      :error="hasError || !!field.validationError"
      :error-messages="getErrorMessages"
      :rules="validationRules"
    >
      <template v-slot:item="{ item }">
        <v-row no-gutters class="pa-2" v-if="usesDynamicPresets">
          <v-col>
            <div
              v-if="displayValueEnabled('identifier') && item.identifier"
              class="option-identifier"
            >
              {{ item.identifier }}
            </div>
            <div
              v-if="displayValueEnabled('title') && item.title"
              class="option-title"
            >
              {{ item.title }}
            </div>
            <div
              v-if="displayValueEnabled('description') && item.description"
              class="option-description"
            >
              {{ item.description }}
            </div>
            <div
              v-if="displayValueEnabled('points') && item.points"
              class="option-points"
            >
              {{ item.points }} points
            </div>
          </v-col>
        </v-row>
        <div v-else>{{ item.value || item }}</div>
      </template>
    </v-autocomplete>

    <!-- Radio buttons -->
    <div v-if="field.type === 'radio'">
      <div class="field-label mb-2">{{ field.label }}</div>
      <v-radio-group v-model="radioValue">
        <v-radio
          v-for="(option, index) in getFieldOptions"
          :key="`radio-${index}`"
          :value="usesDynamicPresets ? option.identifier : option.value"
          :label="getOptionLabel(option)"
        >
        </v-radio>
      </v-radio-group>
    </div>

    <!-- Checkboxes -->
    <div v-if="field.type === 'checkboxes'">
      <div class="field-label mb-2">{{ field.label }}</div>
      <div
        v-for="(option, index) in getFieldOptions"
        :key="`checkbox-${index}`"
      >
        <v-checkbox
          :label="getOptionLabel(option)"
          :value="usesDynamicPresets ? option.identifier : option.value"
          v-model="checkboxValue"
          hide-details
          class="mt-1"
        ></v-checkbox>
      </div>
    </div>

    <!-- Date picker -->
    <v-menu
      v-if="field.type === 'date-picker'"
      ref="dateMenu"
      v-model="dateMenu"
      :close-on-content-click="false"
      :close-on-click="false"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :value="value"
          :label="field.label"
          :placeholder="field.placeholder"
          prepend-inner-icon="calendar_month"
          readonly
          outlined
          dense
          v-bind="attrs"
          v-on="on"
          clearable
          background-color="white"
          @click:clear="updateValue('')"
          :error="hasError || !!field.validationError"
          :error-messages="getErrorMessages"
          :rules="validationRules"
        ></v-text-field>
      </template>
      <v-date-picker
        :value="value"
        @input="updateDateValue"
        scrollable
        no-title
      ></v-date-picker>
    </v-menu>

    <!-- Time picker -->
    <v-menu
      v-if="field.type === 'time-picker'"
      ref="timeMenu"
      v-model="timeMenu"
      :close-on-content-click="false"
      :close-on-click="false"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :value="value"
          :label="field.label"
          :placeholder="field.placeholder"
          prepend-inner-icon="schedule"
          readonly
          outlined
          dense
          v-bind="attrs"
          v-on="on"
          clearable
          @click:clear="updateValue('')"
          :error="hasError || !!field.validationError"
          :error-messages="getErrorMessages"
          :rules="validationRules"
        ></v-text-field>
      </template>
      <v-time-picker :value="value" @input="updateTimeValue"></v-time-picker>
    </v-menu>

    <!-- Switch -->
    <v-switch
      v-if="field.type === 'switch'"
      :label="field.label"
      :input-value="value"
      @change="updateValue"
      class="mt-0"
    ></v-switch>

    <!-- Slider -->
    <div v-if="field.type === 'slider'">
      <div class="field-label">{{ field.label }}</div>
      <v-slider
        :min="field.min || 0"
        :max="field.max || 100"
        :thumb-label="field.thumbLabel"
        :value="value"
        @input="updateValue"
      ></v-slider>
    </div>

    <!-- Table -->
    <div v-if="field.type === 'table'">
      <div class="field-label mb-2">{{ field.label }}</div>
      <custom-form-table
        :component="component"
        :field="field"
        :value="value || [{}]"
        :hasAttemptedSubmit="hasAttemptedSubmit"
        @input="updateValue"
        @edit-state-change="handleTableEditStateChange"
        @validation="handleTableValidation"
        @validation-error="handleTableValidationError"
        @validation-event="handleValidationEvent"
      ></custom-form-table>
    </div>

    <!-- Facebook -->
    <div v-if="field.type === 'facebook'">
      <facebook
        :field="field"
        :value="value || {}"
        @input="updateValue"
      ></facebook>
    </div>

    <!-- Dropzone -->
    <component-renderer
      v-if="field.type === 'dropzone'"
      :field="field"
      :value="value"
      :validation-error="computedValidationError"
      :showValidation="hasAttemptedSubmit"
      @input="updateValue"
      @validation-error="handleValidationError"
      @clear-validation="handleClearValidation"
      @upload-status-change="handleUploadStatusChange"
    ></component-renderer>

    <!-- Headings -->
    <div
      v-if="
        field.type === 'title' ||
          field.type === 'subtitle' ||
          field.type === 'text' ||
          field.type === 'caption'
      "
    >
      <div
        :style="{
          fontSize: `${field.styles.size}px`,
          fontWeight: field.styles.weight,
          fontStyle: field.styles.style,
          color: field.styles.color,
          fontFamily: field.styles.font,
          lineHeight: field.styles.lineHeight,
          height:
            field.type === 'text' ? 'auto' : `${field.styles.boxHeight}px`,
          textAlign: getTextAlign(field.styles.floatText),
          marginLeft: `${field.styles.marginLeft}px` || '0px',
          marginRight: `${field.styles.marginRight}px` || '0px',
          marginTop: `${field.styles.marginTop}px` || '0px',
          marginBottom: `${field.styles.marginBottom}px` || '0px',
          whiteSpace: field.type === 'text' ? 'pre-wrap' : 'normal',
          overflow: field.type === 'text' ? 'visible' : 'hidden',
          maxHeight:
            field.type === 'text' ? 'none' : `${field.styles.boxHeight}px`,
          minHeight: field.type === 'text' ? '20px' : 'auto'
        }"
      >
        <div
          :class="field.type === 'text' ? 'text-display' : ''"
          v-if="field.type === 'text'"
        >
          <div v-for="line in formattedText" :key="'line-' + line.index">
            <span v-if="line.isLineBreakBefore"><br /></span>{{ line.content }}
          </div>
        </div>
        <div v-else>{{ field.styles.value }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomFormTable from "./Table.vue";
import Facebook from "./Facebook.vue";
import ComponentRenderer from "./ComponentRenderer.vue";
import { formatMultilineText } from "@/utils/TextUtils";

export default {
  components: {
    CustomFormTable,
    Facebook,
    ComponentRenderer
  },
  props: {
    field: {
      type: Object,
      required: true
    },
    fieldId: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number, Boolean, Array, Object],
      default: null
    },
    validationError: {
      type: String,
      default: null
    },
    hasAttemptedSubmit: {
      type: Boolean,
      default: false
    },
    component: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      dateMenu: false,
      timeMenu: false,
      dynamicOptions: [],
      isLoadingOptions: false,
      localCheckboxValues: [],
      localValidationError: null
    };
  },

  created() {
    if (this.field) {
      //console.log("[Field] Field type:", this.field.type);

      // Add specific logging for dropzone fields
      if (this.field.type === "dropzone") {
        //console.log(
        //  "[Field] Found dropzone field - should be handled by parent ComponentRenderer"
        //);
      }
    }

    // Initialize checkbox values if needed
    if (
      this.field &&
      this.field.type === "checkboxes" &&
      Array.isArray(this.value)
    ) {
      this.localCheckboxValues = [...this.value];
    }

    // Load dynamic options if needed
    if (this.usesDynamicPresets && this.selectedPresetGroup) {
      this.loadDynamicOptions();
    }

    // Dynamic validation data is loaded on-demand during form submission
    // No need to preload it during component creation
  },
  computed: {
    usesDynamicPresets() {
      return this.field.presets && this.field.presets.presetsType === "dynamic";
    },

    getFieldOptions() {
      if (!this.field.presets) return [];

      if (this.field.presets.presetsType === "static") {
        return this.field.presets.staticOptions || [];
      } else if (this.field.presets.presetsType === "dynamic") {
        return this.dynamicOptions;
      }

      return [];
    },

    getItemText() {
      return this.usesDynamicPresets ? "displayText" : "value";
    },

    getItemValue() {
      return this.usesDynamicPresets ? "identifier" : "value";
    },

    selectedPresetGroup() {
      return this.field.presets && this.field.presets.selectedPresetsGroup;
    },

    // Get selected value for dropdown and radio
    getSelectedValue() {
      if (!this.value) return null;

      if (this.usesDynamicPresets) {
        // For dynamic presets, find the matching object by identifier
        if (typeof this.value === "object" && this.value.identifier) {
          // Value is already a complete object
          return this.value;
        } else {
          // Value is just the identifier, find the full object
          return (
            this.getFieldOptions.find(
              option => option.identifier === this.value
            ) || null
          );
        }
      } else {
        // For static presets or simple values
        const option = this.getFieldOptions.find(opt =>
          typeof opt === "object"
            ? opt.value === this.value
            : opt === this.value
        );
        return option || this.value;
      }
    },

    // Use v-model for checkboxes
    checkboxValue: {
      get() {
        return Array.isArray(this.value) ? this.value : [];
      },
      set(newValue) {
        this.updateValue(newValue);
      }
    },

    radioValue: {
      get() {
        return this.value || "";
      },
      set(newValue) {
        // Update allOptions first
        if (this.field.allOptions) {
          this.field.allOptions.forEach(opt => {
            opt.isSelected = this.usesDynamicPresets
              ? opt.identifier === newValue
              : opt.value === newValue;
          });
        }
        // Then update the value
        this.updateValue(newValue);
      }
    },

    hasError() {
      return (
        this.hasAttemptedSubmit &&
        (this.errorMessages.length > 0 || !!this.computedValidationError)
      );
    },

    // Create a computed property to combine prop and local validation errors
    computedValidationError() {
      return this.localValidationError || this.validationError;
    },

    formattedText() {
      return formatMultilineText(this.field?.styles?.value || "");
    },

    getErrorMessages() {
      // Check for direct validation error on the field object first
      if (this.computedValidationError) {
        return [this.computedValidationError];
      }
      // Otherwise use the computed error messages
      return this.errorMessages;
    },

    errorMessages() {
      const messages = [];

      // Only validate if form submission has been attempted
      if (this.hasAttemptedSubmit) {
        // Check required validation
        if (this.field.required && !this.value) {
          messages.push(`${this.field.label} is required`);
        }

        // Check static validation
        if (
          this.field.validation?.validationFormDataType === "static" &&
          this.field.validation?.staticValidationOptions?.length > 0 &&
          this.value
        ) {
          const isValid = this.field.validation.staticValidationOptions.some(
            option => option.value === this.value
          );
          if (!isValid) {
            messages.push(
              `${this.field.label} is not valid. Please enter a valid ${this.field.label}`
            );
          }
        }

        // Check dynamic validation
        if (
          this.field.validation?.validationFormDataType === "dynamic" &&
          this.field.validation?.selectedFormValidationDataSet &&
          this.value
        ) {
          // Dynamic validation is handled during form submission via validateDynamicOptions
          // No validation in computed properties to prevent performance issues
        }
      }

      return messages;
    },
    validationRules() {
      const rules = [];

      // Add required validation if field is required
      if (this.field.required) {
        rules.push(v => !!v || `${this.field.label} is required`);
      }

      // Add validation against static options if configured
      if (
        this.field.validation?.validationFormDataType === "static" &&
        this.field.validation?.staticValidationOptions?.length > 0
      ) {
        rules.push(this.validateStaticOptions);
      }

      // For dynamic validation, we'll handle it manually via the errorMessages computed property
      // instead of using the rules array since async validation isn't supported in Vuetify rules

      return rules;
    },
    safeTextFieldRules() {
      // For textfields with dynamic validation, return empty rules to prevent any validation during typing
      // All validation will happen during form submission via errorMessages computed property
      if (
        this.field.type === "textfield" &&
        this.field.validation?.validationFormDataType === "dynamic"
      ) {
        return []; // No rules at all for dynamic validation textfields
      }

      // For all other textfields, use the full validation rules
      return this.validationRules;
    },
    validateStaticOptions(v) {
      if (!v) return true; // Skip validation if no value and not required
      const isValid = this.field.validation.staticValidationOptions.some(
        option => option.value === v
      );
      return (
        isValid ||
        `${this.field.label} is not valid. Please enter a valid ${this.field.label}`
      );
    },
    async validateDynamicOptions(v) {
      if (!v) return true; // Skip validation if no value and not required

      try {
        const dataSetId = this.field.validation.selectedFormValidationDataSet;

        if (!dataSetId) {
          return `No validation data set specified for ${this.field.label}`;
        }

        // Use optimized validation for ALL field types with dynamic validation
        const validationResult = await this.$store.dispatch(
          "validateTextFieldValue",
          {
            dataSetId: dataSetId,
            value: v,
            maxClaims: this.field.validation.maxClaims || null,
            fieldLabel: this.field.label || "Field"
          }
        );

        if (!validationResult.isValid) {
          return validationResult.error || `${this.field.label} is not valid.`;
        }

        // Check claims limit
        if (validationResult.claimsExceeded) {
          const claimLimit = validationResult.claimsLimit || 1;
          return claimLimit === 1
            ? `${this.field.label} has already been claimed`
            : `${this.field.label} has reached the claim limit of ${claimLimit}`;
        }

        // Validation events are recorded during form submission only
        return true;
      } catch (error) {
        console.error(`Error validating field ${this.field.label}:`, error);
        return `Error validating ${this.field.label}`;
      }
    }
  },

  watch: {
    selectedPresetGroup: {
      handler() {
        this.loadDynamicOptions();
      },
      immediate: true
    },

    "field.presets.presetsType": {
      handler() {
        this.loadDynamicOptions();
      }
    }
  },
  methods: {
    updateValue(val) {
      // PERFORMANCE OPTIMIZATION: Set global typing state for textfields to prevent cross-component reactivity
      if (this.field && this.field.type === "textfield") {
        window._formTypingActive = true;
        // Clear the typing state after a short delay to allow for rapid typing
        clearTimeout(window._formTypingTimeout);
        window._formTypingTimeout = setTimeout(() => {
          window._formTypingActive = false;
        }, 500); // 500ms delay after last keystroke
      }

      // Clear our local validation error
      if (this.localValidationError) {
        this.localValidationError = null;
      }

      // Emit the value update event
      this.$emit("update:value", val);

      // Emit an event to notify parent to clear validation errors for this field if needed
      this.$emit("clear-validation");
    },

    updateDateValue(date) {
      this.updateValue(date);
      this.dateMenu = false;
    },

    updateTimeValue(time) {
      this.updateValue(time);
      this.timeMenu = false;
    },

    // Handle dropdown selection
    handleDropdownChange(selectedItem) {
      // If nothing is selected, pass null
      if (!selectedItem) {
        this.updateValue(null);
        return;
      }

      // For dynamic presets, store all data except problematic Firebase timestamps
      if (
        this.usesDynamicPresets &&
        selectedItem &&
        typeof selectedItem === "object"
      ) {
        const cleanItem = {};

        // Copy all properties except problematic timestamp fields
        Object.keys(selectedItem).forEach(key => {
          // Exclude problematic timestamp fields that cause Vue Observer issues
          if (
            ![
              "createdAt",
              "importedAt",
              "updatedAt",
              "created",
              "updated",
              "imported"
            ].includes(key)
          ) {
            cleanItem[key] = selectedItem[key];
          }
        });

        // Ensure essential display properties are set
        if (!cleanItem.value) {
          cleanItem.value = cleanItem.identifier;
        }
        if (!cleanItem.text && !cleanItem.displayText) {
          cleanItem.text = cleanItem.identifier;
          cleanItem.displayText = cleanItem.identifier;
        }

        this.updateValue(cleanItem);
      } else {
        // For static presets or simple values, store as-is
        this.updateValue(selectedItem);
      }
    },

    displayValueEnabled(field) {
      return (
        this.field.presets &&
        this.field.presets.valuesToDisplay &&
        this.field.presets.valuesToDisplay.includes(field)
      );
    },

    getOptionValue(option) {
      if (typeof option === "object") {
        return this.usesDynamicPresets ? option.identifier : option.value;
      }
      return option;
    },

    getOptionLabel(option) {
      if (typeof option === "object") {
        if (this.usesDynamicPresets) {
          // Build a label from the fields that should be displayed
          const parts = [];
          if (this.displayValueEnabled("identifier")) {
            parts.push(option.identifier);
          }
          if (this.displayValueEnabled("title")) {
            parts.push(option.title);
          }
          if (this.displayValueEnabled("description")) {
            parts.push(option.description);
          }
          return parts.join(" - ");
        }
        return option.value;
      }
      return option;
    },

    getTextAlign(floatText) {
      switch (floatText) {
        case "text-left":
          return "left";
        case "text-center":
          return "center";
        case "text-right":
          return "right";
        default:
          return "left";
      }
    },

    async loadDynamicOptions() {
      // Only load if using dynamic presets
      if (!this.usesDynamicPresets || !this.selectedPresetGroup) {
        this.dynamicOptions = [];
        return;
      }

      // CRITICAL: Do NOT load large datasets for textfields with dynamic validation
      // Textfields are for validation only, not for populating options
      // Loading 20K+ records for a textfield validation is unnecessary and causes performance issues
      if (
        this.field.type === "textfield" &&
        this.field.validation?.validationFormDataType === "dynamic"
      ) {
        console.log(
          `🚀 OPTIMIZATION: Skipping preset records load for textfield '${this.field.label}' - validation will use optimized single-record lookup`
        );
        this.dynamicOptions = [];
        return;
      }

      // Avoid loading multiple times at once
      if (this.isLoadingOptions) return;

      // PERFORMANCE OPTIMIZATION: Skip expensive Vuex operations during active typing
      // This prevents cross-component reactivity that causes all form components to re-evaluate
      if (window._formTypingActive) {
        console.log(
          "🚀 [Field] Skipping loadDynamicOptions during active typing to prevent cross-component reactivity"
        );
        return;
      }

      this.isLoadingOptions = true;

      try {
        // Call the Vuex action to get preset records
        const records = await this.$store.dispatch(
          "getPresetRecords",
          this.selectedPresetGroup
        );

        // Process the records
        if (Array.isArray(records)) {
          this.dynamicOptions = records
            .filter(item => item.status === "Active")
            .map(item => ({
              ...item,
              value: item.identifier, // Ensure value is set for compatibility
              text: this.generateDisplayText(item), // Set text for display
              displayText: this.generateDisplayText(item)
            }));

          // Emit the loaded options
          this.$root.$emit("dynamic-options-loaded", {
            fieldId: this.fieldId,
            options: this.dynamicOptions
          });
        } else {
          this.dynamicOptions = [];
        }
      } catch (error) {
        console.error("Error loading preset options:", error);
        this.dynamicOptions = [];
      } finally {
        this.isLoadingOptions = false;
      }
    },

    generateDisplayText(item) {
      // Generate display text based on selected values to display
      const displayValues = this.field.presets?.valuesToDisplay || [];

      const displayParts = displayValues
        .map(val => {
          switch (val) {
            case "identifier":
              return item.identifier;
            case "title":
              return item.title;
            case "description":
              return item.description;
            case "points":
              return item.points ? `${item.points} points` : "";
            default:
              return "";
          }
        })
        .filter(Boolean);

      return displayParts.join(" • ");
    },

    isOptionSelected(option) {
      if (!this.value || !Array.isArray(this.value)) return false;
      const optionValue = this.getOptionValue(option);
      return this.value.includes(optionValue);
    },

    toggleOption(option) {
      const optionValue = this.getOptionValue(option);
      let newValue = Array.isArray(this.value) ? [...this.value] : [];

      if (newValue.includes(optionValue)) {
        newValue = newValue.filter(v => v !== optionValue);
      } else {
        newValue.push(optionValue);
      }

      this.updateValue(newValue);
    },

    handleTableEditStateChange({ fieldId, isEditing }) {
      this.$emit("table-edit-state-change", {
        fieldId: this.fieldId, // Use the Field's fieldId for consistency with Main.vue
        isEditing
      });
    },

    handleValidationError(errorData) {
      // If we receive a validation error, set it on this field
      if (errorData && errorData.error) {
        // Set validation error in local state instead of directly on the prop
        this.localValidationError = errorData.error;

        // Forward the validation error to the parent with fieldId
        this.$emit("validation-error", {
          fieldId: this.fieldId,
          error: errorData.error
        });
      }
    },

    handleClearValidation() {
      // Clear validation error state in local property
      this.localValidationError = null;

      // Forward the clear validation event to the parent
      this.$emit("clear-validation");
    },

    handleTableValidation(isValid, errorMessage) {
      if (!isValid) {
        // Set validation error in local state
        this.localValidationError = errorMessage;

        // Forward validation error to parent
        this.$emit("validation-error", {
          fieldId: this.fieldId,
          error: errorMessage
        });
      } else {
        // Clear validation error when table becomes valid
        if (this.localValidationError) {
          this.localValidationError = null;
          this.$emit("validation-error", {
            fieldId: this.fieldId,
            error: null
          });
        }
      }
    },

    handleTableValidationError(errorData) {
      // Forward table validation errors directly - don't override with field's own fieldId
      this.$emit("validation-error", errorData);
    },

    handleUploadStatusChange(status) {
      // Forward the upload status change to the parent
      this.$emit("upload-status-change", status);
    },

    handleValidationEvent(validationEvent) {
      // Forward the validation event to the parent
      this.$emit("validation-event", validationEvent);
    }
  }
};
</script>

<style scoped>
.field-container {
  width: 100%;
}

.field-label {
  font-family: "Figtree", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.option-identifier {
  font-family: "Figtree", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #494949de;
  line-height: 1;
}

.option-title {
  font-family: "Figtree", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #000000de;
  line-height: 1.3;
}

.option-description {
  font-family: "Figtree", sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #a1a1a1;
  line-height: 1;
}

.option-points {
  font-family: "Figtree", sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #a1a1a1;
  line-height: 1;
}

.text-content {
  margin-bottom: 0px;
  margin-top: 0px;
}

.text-display {
  white-space: pre-wrap !important;
  word-wrap: break-word;
  overflow-wrap: break-word;
  height: auto !important;
}
</style>
